import React, { useEffect, useMemo, useState } from 'react';

import { FieldArray, Formik } from 'formik';
import * as Yup from 'yup';

import BudgetAttachmentFields from '../BudgetAttachmentFields';
import {
    ImpactSelector,
    ImpactCategorySelector,
    ProbabilitySelector,
    StatusSelector,
    TypesSelector,
    GeneralIssuesSelector,
    GeneralDecisionsSelector,
    VenueSelector,
} from '../Selectors';

import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';
import ModalFooter from 'CommonComponents/ModalFooter';
import TextareaFormField from 'CommonComponents/TextareaFormField';
import TextFormField from 'CommonComponents/TextFormField';
import ToggleFormField from 'CommonComponents/ToggleFormField';
import UserSelectorInput from 'CommonComponents/UserSelectorInput';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import { useUsers } from 'Hooks/useUsers';

import MinusIcon from 'Assets/icons/minus.svg';
import PlusIcon from 'Assets/icons/plus.svg';

const DEFAULT_ACTION = { id: null, description: '', owner_id: '', date: '', completed_date: '' };

const GeneralRiskSchema = Yup.object().shape(
    {
        description: Yup.string().required($t('This value is required')),
        detailed_description: Yup.string(),
        impact_description: Yup.string(),
        mitigation: Yup.string(),
        budget_impact: Yup.bool(),
        AFC: Yup.bool(),
        amount: Yup.number()
            .nullable()
            .when('budget_impact', {
                is: true,
                then: Yup.number().min(0, $t('Amount must be zero or greater')).required($t('This value is required when Budget Impact is enabled')),
            }),
        budget_department_key: Yup.string().nullable(),
        budget_department_type: Yup.string().nullable(),
        budget_department_id: Yup.string().nullable(),
        budget_line_type: Yup.string().nullable(),
        budget_line_id: Yup.string().nullable(),
        owner_id: Yup.string(),
        general_risk_type_id: Yup.string(),
        general_impact_id: Yup.string().when('general_probability_id', {
            is: (value) => !!value,
            then: Yup.string().required($t('This value is required when probability is selected')),
        }),
        general_probability_id: Yup.string().when('general_impact_id', {
            is: (value) => !!value,
            then: Yup.string().required($t('This value is required when impact is selected')),
        }),
        controlled_impact_id: Yup.string().when('controlled_probability_id', {
            is: (value) => !!value,
            then: Yup.string().required($t('This value is required when controlled probability is selected')),
        }),
        controlled_probability_id: Yup.string().when('controlled_impact_id', {
            is: (value) => !!value,
            then: Yup.string().required($t('This value is required when controlled impact is selected')),
        }),
        general_status_id: Yup.string(),
        impact_category_ids: Yup.array(Yup.string()),
        general_issue_ids: Yup.array(Yup.string()),
        general_decision_ids: Yup.array(Yup.string()),
        venue_ids: Yup.array().of(Yup.string()),
        actions: Yup.array().of(
            Yup.object().shape({
                description: Yup.string().required($t('This value is required')),
                owner_id: Yup.string(),
                date: Yup.date().nullable(),
                completed_date: Yup.date().nullable(),
            }),
        ),
    },
    [
        ['general_impact_id', 'general_probability_id'],
        ['controlled_impact_id', 'controlled_probability_id'],
    ],
);

export default function GeneralRiskForm(props) {
    const [priority, setPriority] = useState(null);
    const [residualPriority, setResidualPriority] = useState(null);

    const { users } = useUsers();

    const confirmDisableBudgetImpact = useConfirmationModal({
        title: $t('Turn off Budget Impact'),
        warning: $t('You must de-select the budget line items and remove the amount prior to turning of Budget Impact'),
    });

    const budgetDepartment = useMemo(() => {
        let departmentId = null;
        let departmentType = null;

        if (props.risk?.budget_department_type && props.risk?.budget_department_id) {
            departmentId = props.risk.budget_department_id;
            departmentType = props.risk.budget_department_type;
        } else if (props.budget_department_type && props.budget_department_id) {
            departmentId = props.budget_department_id;
            departmentType = props.budget_department_type;
        }

        return { id: departmentId, type: departmentType, key: departmentId && departmentType ? `${departmentType}::${departmentId}` : null };
    }, [props.budget_department_type, props.budget_department_id, props.risk]);

    const getPriority = (impactId, probabilityId) => axios.get(props.getPriorityUrl, { params: { impactId, probabilityId } });

    return (
        <Formik
            enableReinitialize
            initialValues={{
                description: props.risk?.description || '',
                detailed_description: props.risk?.detailed_description || '',
                impact_description: props.risk?.impact_description || '',
                mitigation: props.risk?.mitigation || '',
                owner_id: props.risk?.owner_id || '',
                budget_impact: props.budget_department_type && props.budget_department_id ? true : !!props.risk?.budget_impact,
                amount: props.risk?.amount || 0,
                AFC: props.risk?.AFC || false,
                budget_department_key: budgetDepartment.key || '',
                budget_department_type: budgetDepartment.type || '',
                budget_department_id: budgetDepartment.id || '',
                budget_line_id: props.risk?.budget_line_id || null,
                budget_line_type: props.risk?.budget_line_type || null,
                general_risk_type_id: props.risk?.general_risk_type_id || '',
                general_impact_id: props.risk?.general_impact_id || '',
                general_probability_id: props.risk?.general_probability_id || '',
                controlled_impact_id: props.risk?.controlled_impact_id || '',
                controlled_probability_id: props.risk?.controlled_probability_id || '',
                general_status_id: props.risk?.general_status_id || '',
                impact_category_ids: props.risk?.impact_category_ids?.map(String) || [],
                general_issue_ids: props.risk?.general_issue_ids?.map(String) || [],
                general_decision_ids: props.risk?.general_decision_ids?.map(String) || [],
                venue_ids: props.risk?.venue_ids?.map(String) || [],
                actions:
                    props.risk?.actions?.map((action) => ({
                        ...action,
                        description: action.description ?? DEFAULT_ACTION.description,
                        owner_id: action.owner_id ?? DEFAULT_ACTION.owner_id,
                        date: action.date ?? DEFAULT_ACTION.date,
                    })) || [],
            }}
            validationSchema={GeneralRiskSchema}
            onSubmit={(values, actions) => {
                if (props.risk?.id) {
                    if (props.currentVenue) {
                        // if the risk is being edited from a venue context, we want to keep the risk associated to the current venue only, as we don't want the user to edit the venues from this form
                        values.venue_ids = props.risk?.venue_ids?.map(String) || [];
                    }

                    const updateUrl = props.updateUrl.replace('GENERAL_RISK_ID', props.risk.id);

                    axios
                        .put(updateUrl, values)
                        .then((res) => {
                            actions.resetForm();
                            props.onSuccess();
                        })
                        .catch((e) => {
                            e?.response?.data?.errors && actions.setErrors(e.response.data.errors);
                        })
                        .finally(() => {
                            actions.setSubmitting(false);
                        });
                } else {
                    if (props.currentVenue) {
                        // if the risk is being created from a venue context, we want to automatically associate the risk to the current venue, as we don't want the user to select the venues from this form
                        values.venue_ids = [props.currentVenue.id];
                    }

                    axios
                        .post(props.storeUrl, values)
                        .then((res) => {
                            actions.resetForm();
                            props.onSuccess();
                        })
                        .catch((e) => {
                            e?.response?.data?.errors && actions.setErrors(e?.response?.data?.errors);
                        })
                        .finally(() => {
                            actions.setSubmitting(false);
                        });
                }
            }}
        >
            {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => {
                useEffect(() => {
                    if (!values.general_impact_id || !values.general_probability_id) {
                        return setPriority(null);
                    }

                    getPriority(values.general_impact_id, values.general_probability_id)
                        .then(({ data }) => {
                            setPriority(data);
                        })
                        .catch((e) => {
                            console.error(e);
                        });
                }, [values.general_impact_id, values.general_probability_id]);

                useEffect(() => {
                    if (!values.controlled_impact_id || !values.controlled_probability_id) {
                        return setResidualPriority(null);
                    }

                    getPriority(values.controlled_impact_id, values.controlled_probability_id)
                        .then(({ data }) => {
                            setResidualPriority(data);
                        })
                        .catch((e) => {
                            console.error(e);
                        });
                }, [values.controlled_impact_id, values.controlled_probability_id]);

                useEffect(() => {
                    if (!values.budget_impact) {
                        setFieldValue('budget_department_key', '');
                        setFieldValue('budget_department_id', '');
                        setFieldValue('budget_department_type', '');
                        setFieldValue('budget_line_id', null);
                        setFieldValue('budget_line_type', null);
                        return;
                    }

                    setFieldValue('budget_department_key', budgetDepartment.key || '');
                    setFieldValue('budget_department_id', budgetDepartment.id || '');
                    setFieldValue('budget_department_type', budgetDepartment.type || '');
                }, [values.budget_impact]);

                const toggleBudgetImpact = (value) => {
                    if (!value && (values.budget_line_id || values.amount)) {
                        confirmDisableBudgetImpact();
                        return;
                    }

                    setFieldValue('budget_impact', value);
                };

                return (
                    <>
                        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                            <TextFormField
                                label={$t('Description')}
                                required={true}
                                type="text"
                                name="description"
                                placeholder={$t('Enter risk description')}
                            ></TextFormField>
                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                <label className="text-input__label">{$t('Owner')}</label>
                                <UserSelectorInput
                                    selectedId={values.owner_id}
                                    onChange={(value) => setFieldValue('owner_id', value)}
                                    usersList={users}
                                    fetchIfNoUsers={false}
                                />
                                {errors.owner_id && touched.owner_id ? <div className="tw-text-red-500 tw-text-sm">{errors.owner_id}</div> : null}
                            </div>
                        </div>

                        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                            <TypesSelector
                                name="general_risk_type_id"
                                types={props.datasets.types}
                                errors={errors.general_risk_type_id}
                                touched={touched.general_risk_type_id}
                            />
                            <StatusSelector
                                name="general_status_id"
                                statuses={props.datasets.statuses}
                                errors={errors.general_status_id}
                                touched={touched.general_status_id}
                            />
                        </div>

                        {props.allowVenueSelection && (
                            <div className="tw-grid tw-grid-cols-1">
                                <VenueSelector
                                    venuesOptions={props.datasets.venuesOptions}
                                    values={values}
                                    onChange={(venues) => setFieldValue('venue_ids', venues?.map((venue) => venue.value) || [])}
                                    disabled={isSubmitting}
                                    errors={errors.venue_ids}
                                    touched={touched.venue_ids}
                                />
                            </div>
                        )}

                        <div className="tw-grid tw-grid-cols-1">
                            <TextareaFormField
                                label={$t('Detailed description')}
                                as="textarea"
                                type="text"
                                name="detailed_description"
                                placeholder={$t("Enter risk's detailed description")}
                            ></TextareaFormField>
                        </div>

                        <div className="tw-w-full tw-border-b tw-border-2 tw-border-grey"></div>

                        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                            <ImpactSelector
                                name="general_impact_id"
                                impacts={props.datasets.impacts}
                                errors={errors.general_impact_id}
                                touched={touched.general_impact_id}
                            />
                            <ProbabilitySelector
                                name="general_probability_id"
                                probabilities={props.datasets.probabilities}
                                errors={errors.general_probability_id}
                                touched={touched.general_probability_id}
                            />
                        </div>

                        <div className="tw-grid tw-grid-cols-1">
                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                <span className="tw-text-charcoal tw-text-sm">
                                    Classification{' '}
                                    <span className="tw-font-semibold">{priority?.letter || 'Select impact and probability to show classification'}</span>
                                </span>
                                {priority && <div className="tw-py-3 tw-w-3/12" style={{ background: priority?.color }}></div>}
                            </div>
                        </div>

                        <div className="tw-grid tw-grid-cols-1">
                            <ImpactCategorySelector
                                name="impact_category_ids"
                                categories={props.datasets.impactCategories}
                                errors={errors.impact_category_ids}
                                touched={touched.impact_category_ids}
                            />
                        </div>

                        <div className="tw-grid tw-grid-cols-1">
                            <TextareaFormField
                                label={$t('Impact description')}
                                as="textarea"
                                type="text"
                                name="impact_description"
                                placeholder={$t('Enter impact description')}
                            ></TextareaFormField>
                        </div>

                        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                <ToggleFormField
                                    label="Budget Impact"
                                    type="checkbox"
                                    name="budget_impact"
                                    id="budget_impact"
                                    checked={values.budget_impact}
                                    onChange={() => toggleBudgetImpact(!values.budget_impact)}
                                ></ToggleFormField>
                            </div>
                        </div>

                        {values.budget_impact && (
                            <BudgetAttachmentFields
                                departmentsOptions={props.datasets.departmentsOptions}
                                fetchBudgetLinesUrl={props.fetchGeneralRiskBudgetLinesUrl}
                                values={values}
                                setFieldValue={setFieldValue}
                                errors={errors}
                                touched={touched}
                                lockDepartment={!!(props.budget_department_type && props.budget_department_id)}
                                afcField="AFC"
                                afcLabel={$t('Include in AFC')}
                                departmentAside={
                                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                        <TextFormField label={$t('Amount')} type="number" name="amount"></TextFormField>
                                    </div>
                                }
                            />
                        )}

                        <div className="tw-w-full tw-border-b tw-border-2 tw-border-grey"></div>

                        <div className="tw-grid tw-grid-cols-1">
                            <TextareaFormField label={$t('Mitigation')} as="textarea" type="text" name="mitigation"></TextareaFormField>
                        </div>

                        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                            <ImpactSelector
                                name="controlled_impact_id"
                                label="Select a controlled impact"
                                impacts={props.datasets.impacts}
                                errors={errors.controlled_impact_id}
                                touched={touched.controlled_impact_id}
                            />
                            <ProbabilitySelector
                                name="controlled_probability_id"
                                label="Select a controlled probability"
                                probabilities={props.datasets.probabilities}
                                errors={errors.controlled_probability_id}
                                touched={touched.controlled_probability_id}
                            />
                        </div>

                        <div className="tw-grid tw-grid-cols-1">
                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                <span className="tw-text-charcoal tw-text-sm">
                                    Classification - After Mitigation{' '}
                                    <span className="tw-font-semibold">
                                        {residualPriority?.letter || 'Select impact and probability to show classification'}
                                    </span>
                                </span>
                                {residualPriority && <div className="tw-py-3 tw-w-3/12" style={{ background: residualPriority?.color }}></div>}
                            </div>
                        </div>

                        <div className="tw-w-full tw-border-b tw-border-2 tw-border-grey"></div>

                        <FieldArray
                            name="actions"
                            render={(arrayHelpers) => (
                                <>
                                    {!!values.actions?.length &&
                                        values.actions.map((action, index) => {
                                            const actionErrors = errors.actions && errors.actions[index];
                                            const actionTouched = touched.actions && touched.actions[index];
                                            return (
                                                <div
                                                    className="tw-flex tw-flex-col sm:tw-flex-row tw-space-y-4 sm:tw-space-y-0 tw-space-x-0 sm:tw-space-x-4"
                                                    key={index}
                                                >
                                                    <div className="tw-w-full">
                                                        <TextareaFormField
                                                            label={$t('Action')}
                                                            required={true}
                                                            as="textarea"
                                                            name={`actions.${index}.description`}
                                                            placeholder={$t('Enter action description')}
                                                        ></TextareaFormField>
                                                    </div>
                                                    <div className="tw-grid tw-grid-rows-2 tw-gap-4 tw-w-ull">
                                                        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                                <label className="text-input__label">{$t('Owner')}</label>
                                                                <UserSelectorInput
                                                                    selectedId={action.owner_id}
                                                                    onChange={(value) => setFieldValue(`actions.${index}.owner_id`, value)}
                                                                    usersList={users}
                                                                    fetchIfNoUsers={false}
                                                                />
                                                                {actionErrors?.owner_id && actionTouched?.owner_id ? (
                                                                    <div className="tw-text-red-500 tw-text-sm">{actionErrors?.owner_id}</div>
                                                                ) : null}
                                                            </div>
                                                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                                <label className="text-input__label">{$t('Due Date')}</label>
                                                                <DatePickerField
                                                                    name={`actions.${index}.date`}
                                                                    valueFormat="yyyy-MM-dd"
                                                                    dateFormat="yyyy-MM-dd"
                                                                    className={
                                                                        actionErrors?.date && actionTouched?.date
                                                                            ? 'tw-border tw-border-red-500 text-input'
                                                                            : 'tw-border-0 text-input'
                                                                    }
                                                                    value={values?.actions[index]?.date}
                                                                    onChange={setFieldValue}
                                                                />
                                                                {actionErrors?.date && actionTouched?.date && (
                                                                    <div className="tw-text-red-500 tw-text-sm">{actionErrors?.date}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="tw-grid tw-grid-cols-1">
                                                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                                <label className="text-input__label">{$t('Completed')}</label>
                                                                <DatePickerField
                                                                    name={`actions.${index}.completed_date`}
                                                                    className={
                                                                        actionErrors?.completed_date && actionTouched?.completed_date
                                                                            ? 'tw-border tw-border-red-500 text-input'
                                                                            : 'tw-border-0 text-input'
                                                                    }
                                                                    value={values?.actions[index]?.completed_date}
                                                                    onChange={setFieldValue}
                                                                />
                                                                {actionErrors?.completed_date && actionTouched?.completed_date && (
                                                                    <div className="tw-text-red-500 tw-text-sm">{actionErrors?.completed_date}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="">
                                                        <Button small variant="danger" className="mt-4" onClick={() => arrayHelpers.remove(index)}>
                                                            <MinusIcon className="tw-w-3.5 tw-h-3.5" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    <div className="tw-flex">
                                        <Button small variant="primary" onClick={() => arrayHelpers.push(DEFAULT_ACTION)}>
                                            <PlusIcon className="tw-w-3 tw-h-3" aria-hidden="true" />
                                            {$t('Add new action')}
                                        </Button>
                                    </div>
                                </>
                            )}
                        />

                        <div className="tw-w-full tw-border-b tw-border-2 tw-border-grey"></div>

                        <div className="tw-grid tw-grid-cols-1 tw-gap-4">
                            <h6 className="tw-text-xl tw-text-charcoal tw-font-semibold">Inter-dependencies</h6>
                            <GeneralIssuesSelector
                                name="general_issue_ids"
                                issues={props.datasets.issues}
                                errors={errors.general_issue_ids}
                                touched={touched.general_issue_ids}
                            />
                            <GeneralDecisionsSelector
                                name="general_decision_ids"
                                decisions={props.datasets.decisions}
                                errors={errors.general_decision_ids}
                                touched={touched.general_decision_ids}
                            />
                        </div>

                        <ModalFooter
                            cancelButtonText={$t('Cancel')}
                            primaryButtonText={$t('Save')}
                            deleteButtonText={$t('Delete')}
                            onCancel={props.onCancel}
                            onDelete={props.onDelete}
                            handleSubmit={handleSubmit}
                            isSubmitting={isSubmitting}
                            submitButtonVariant={'primary'}
                            allowDelete={!!props.risk?.id}
                        ></ModalFooter>
                    </>
                );
            }}
        </Formik>
    );
}
